import React, { useState, useEffect } from "react";
import { fetchFaqMatches } from "../utils/ai";
import FAQCard from "./FAQCard";
import { getCurrentUser } from "../utils/api";

export default function FAQAI({ companyId }) {
  const [open, setOpen] = useState(false);
  const [inputText, setInputText] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFaq, setSelectedFaq] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const u = await getCurrentUser();
        setCurrentUser(u);
      } catch {
        setCurrentUser(null);
      }
    })();
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!inputText.trim()) {
      setError("Please type a question.");
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const matches = await fetchFaqMatches({
        companyId,
        queryText: inputText,
        userId: null,
      });
      setResults(matches);
    } catch (err) {
      console.error(err);
      setError("Search failed. Check console for details.");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClickSuggestion = (faq) => {
    setSelectedFaq(faq);
  };

  const closeModal = () => {
    setSelectedFaq(null);
  };

  return (
    <>
      {/* Floating chat bubble button */}
      <button
        className="faqai_toogle-button"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          width: "60px",
          height: "60px",
          borderRadius: "50%",
          backgroundColor: "#007bff",
          color: "white",
          border: "none",
          boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
          cursor: "pointer",
          fontSize: "24px",
          zIndex: 1000,
        }}
      >
        💬
      </button>

      {/* Chat bubble opened: show search input + suggestions */}
      {open && (
        <div
          className="faqai_panel"
          style={{
            position: "fixed",
            bottom: "100px",
            right: "20px",
            width: "320px",
            maxHeight: "500px",
            backgroundColor: "white",
            border: "1px solid #ddd",
            borderRadius: "8px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
            padding: "12px",
            display: "flex",
            flexDirection: "column",
            zIndex: 1000,
          }}
        >
          <form className="faqai_search-form" onSubmit={handleSearch} style={{ marginBottom: "8px" }}>
            <input
              className="faqai_search-input"
              type="text"
              placeholder="Ask a question..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                marginBottom: "6px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                fontSize: "16px",
              }}
            />

            <button
              className="faqai_search-btn"
              type="submit"
              disabled={loading}
              style={{
                width: "100%",
                padding: "8px",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              {loading ? "Searching..." : "Search"}
            </button>
          </form>

          {error && (
            <div className="faqai_error" style={{ color: "red", marginBottom: "8px" }}>{error}</div>
          )}

          <div className="faqai_results" style={{ overflowY: "auto", flexGrow: 1 }}>
            {results.length > 0 ? (
              results.map((item) => (
                <div
                  className="faqai_suggestion"
                  key={item.id}
                  onClick={() => handleClickSuggestion(item)}
                  style={{
                    marginBottom: "12px",
                    padding: "8px",
                    border: "1px solid #eee",
                    borderRadius: "4px",
                    backgroundColor: "#f9f9f9",
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  <strong className="faqai_suggestion-question"style={{ fontSize: "15px" }}>
                    Q: {item.question}
                  </strong>
                  <p className="faqai_suggestion-answer" style={{ margin: "4px 0" }}>A: {item.answer}</p>
                  <small className="faqai_suggestion-score" style={{ color: "#666" }}>
                    (score: {item.score})
                  </small>
                </div>
              ))
            ) : (
              !loading && (
                <div style={{ color: "#666", fontSize: "14px" }}>
                  No results yet.
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* ----------------- Bootstrap 5 Modal ----------------- */}
      {selectedFaq && (
        <div
          className="faqai_modal modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex={-1}
          role="dialog"
        >
          <div
            className="faqai_modal-dialog modal-dialog modal-dialog-centered"
            role="document"
          >
            <div className="faqai_modal-content modal-content">
              <div className="faqai_modal-header modal-header">
                <h5 className="faqai_modal-title modal-title">FAQ Details</h5>
                <button
                  type="button"
                  className="faqai_modal-close btn-close"
                  aria-label="Close"
                  onClick={closeModal}
                />
              </div>
              <div className="faqai_modal-body modal-body">
                {/* Render the full‐featured FAQCard inside the modal */}
                <FAQCard
                  faq={selectedFaq}
                  currentUser={currentUser}
                  onFaqDeleted={() => {
                    closeModal();
                  }}
                  onFaqUpdated={(partialFaq) => {
                    // 1) Merge partialFaq into results array
                    setResults((prev) =>
                      prev.map((f) =>
                        f.id === partialFaq.id
                          ? { ...f, ...partialFaq }
                          : f
                      )
                    );
                    // 2) Merge into selectedFaq so modal stays in sync
                    setSelectedFaq((prev) =>
                      prev.id === partialFaq.id
                        ? { ...prev, ...partialFaq }
                        : prev
                    );
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
